import { AnimatePresence, motion } from "framer-motion";
import { Edit2, Filter, Plus, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { logDev } from "services/CommonService";
import { UserService } from "services/UserService";
import { toast } from "sonner";
import DynamicUserForm from "../components/users/DynamicUserForm";
import { User, UserRole } from "../types/user";

const Users = () => {
  const [users, setUsers] = useState<User[]>([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState<UserRole | "all">("all");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const { fetchUsers } = UserService();
  useEffect(() => {
    const getUsers = async () => {
      const res = await fetchUsers(10, 0, {});
      setUsers(res.users);
    };

    getUsers();
  }, []);

  const roles: UserRole[] = [
    "admin_system",
    "admin_super",
    "stakeholder_read",
    "stylist",
    "receptionist",
    "client",
    "customer_basic",
    "customer_premium",
    "customer_vip",
    "provider_individual",
    "provider_business",
    "provider_enterprise",
    "staff_support",
    "staff_delivery",
    "staff_manager",
    "staff_hr",
  ];

  const getRoleDisplayName = (role: UserRole) => {
    logDev(role);
    return (
      role !== null &&
      role
        .toString()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase())
    );
  };

  const filteredUsers = users.filter((user) => {
    if (user !== null) {
      const matchesSearch =
        user.name.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toString().toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = selectedRole === "all" || user.role === selectedRole;
      return matchesSearch && matchesRole;
    }
    return [];
  });

  const handleDelete = (userId: string) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      setUsers(users.filter((u) => u.id !== userId));
      toast.success("User deleted successfully");
    }
  };

  const handleStatusToggle = (userId: string) => {
    setUsers(
      users.map((user) =>
        user.id === userId
          ? {
              ...user,
              status: user.status === "active" ? "inactive" : "active",
            }
          : user
      )
    );
    toast.success("User status updated successfully");
  };

  const handleSave = (userData: any) => {
    if (editingUser) {
      setUsers(
        users.map((u) =>
          u.id === editingUser.id ? { ...editingUser, ...userData } : u
        )
      );
      toast.success("User updated successfully");
    } else {
      const newUser: User = {
        id: Math.random().toString(),
        ...userData,
        createdAt: new Date().toISOString(),
        status: "active" as const,
      };
      setUsers([...users, newUser]);
      toast.success("User added successfully");
    }
    setIsAddModalOpen(false);
    setEditingUser(null);
  };

  const getRoleCategory = (role: UserRole) => {
    if (role !== null && role.startsWith("admin")) return "admin";
    if (role !== null && role.startsWith("staff")) return "staff";
    if ((role !== null && role.startsWith("customer")) || role === "client")
      return "customer";
    if (role !== null && role.startsWith("provider")) return "provider";
    if ((role && role === "stylist") || role === "receptionist")
      return "service";
    return "customer";
  };

  const getRoleCategoryColor = (role: UserRole) => {
    const category = getRoleCategory(role);
    switch (category) {
      case "admin":
        return "bg-red-100 text-red-800";
      case "staff":
        return "bg-blue-100 text-blue-800";
      case "service":
        return "bg-green-100 text-green-800";
      case "customer":
        return "bg-purple-100 text-purple-800";
      case "provider":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Users Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage user accounts and their roles across the platform
          </p>
        </div>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-gray-900">{users.length}</div>
          <div className="text-sm text-gray-500">Total Users</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-green-600">
            {users.filter((u) => u.status === "active").length}
          </div>
          <div className="text-sm text-gray-500">Active Users</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-blue-600">
            {users.filter((u) => getRoleCategory(u.role) === "service").length}
          </div>
          <div className="text-sm text-gray-500">Service Providers</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-purple-600">
            {users.filter((u) => getRoleCategory(u.role) === "customer").length}
          </div>
          <div className="text-sm text-gray-500">Customers</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={selectedRole}
            onChange={(e) =>
              setSelectedRole(e.target.value as UserRole | "all")
            }
            className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="all">All Roles</option>
            {roles.map((role) => (
              <option key={role} value={role}>
                {getRoleDisplayName(role)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created At
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            <AnimatePresence>
              {filteredUsers.map((user) => (
                <motion.tr
                  key={user.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-primary-600 font-medium text-sm">
                          {user.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleCategoryColor(
                        user.role
                      )}`}
                    >
                      {getRoleDisplayName(user.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleStatusToggle(user.id)}
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full transition-colors ${
                        user.status === "active"
                          ? "bg-green-100 text-green-800 hover:bg-green-200"
                          : "bg-red-100 text-red-800 hover:bg-red-200"
                      }`}
                    >
                      {user.status}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.createdat).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => {
                        setEditingUser(user);
                        setIsAddModalOpen(true);
                      }}
                      className="text-primary-600 hover:text-primary-900 mr-4 p-1 rounded hover:bg-primary-50"
                      title="Edit user"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                      title="Delete user"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </motion.tr>
              ))}
            </AnimatePresence>
          </tbody>
        </table>

        {users.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500">
              No users found matching your criteria
            </div>
          </div>
        )}
      </div>

      {/* Dynamic User Form Modal */}
      {isAddModalOpen && (
        <DynamicUserForm
          editingUser={editingUser}
          onSave={handleSave}
          onCancel={() => {
            setIsAddModalOpen(false);
            setEditingUser(null);
          }}
        />
      )}
    </div>
  );
};

export default Users;
